/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Steps } from '@/types';
import { AssistantContext } from '@/contexts';
import {
  filiationInconnueAutorisee,
  ROLES_PARENTS_PAR_TYPE_ACTE,
  TYPE_COMPONENT_BY_TYPE_ACTE,
} from '@/constants';

import InformationsParent from './InformationsParent';

const StepParents = () => {
  const { data, onChange, errors, selectedTypeActe } =
    React.useContext(AssistantContext);

  /** Tranche d'erreurs de cette étape, regroupée par parent */
  const errorsStep = errors?.[Steps.Parents] ?? {};

  /**
   * L'étape Parents est partagée par tous les types d'acte (décision produit) :
   * seul le sens d'une filiation non renseignée change (« décédé » en
   * naissance, « inconnu » en reconnaissance).
   */
  const typeComponent = selectedTypeActe
    ? TYPE_COMPONENT_BY_TYPE_ACTE[selectedTypeActe]
    : undefined;

  /**
   * La profession des parents est saisie partout SAUF en reconnaissance :
   * `ec_actr` n'a aucune colonne de profession et la fiche de reconnaissance ne
   * l'affiche pas. La naissance et le décès la portent (`ai_profession` de
   * l'individu), et la maquette du décès la demande explicitement.
   */
  const showProfession = selectedTypeActe !== 'reconnaissance';

  /**
   * Décès : un parent décédé n'a ni lieu de naissance ni profession à saisir
   * (AD-13025).
   */
  const griserSiDecede = selectedTypeActe === 'deces';

  /**
   * Rôles des deux parents pour ce type d'acte : « Père » / « Mère » en
   * reconnaissance, `undefined` en naissance (« PARENT 1 » / « PARENT 2 »).
   * Sa présence commande aussi le verrouillage de la civilité, qui est alors
   * déduite du rôle et préremplie par `AssistantContextProvider`.
   */
  const roles = selectedTypeActe
    ? ROLES_PARENTS_PAR_TYPE_ACTE[selectedTypeActe]
    : undefined;

  /**
   * Un parent auteur de la reconnaissance reconnaît l'enfant : il ne peut pas
   * être déclaré inconnu, et la case ne doit donc pas lui être proposée. Sans
   * auteur (acte de naissance), les deux parents restent libres.
   */
  const inconnuPossible = (numeroParent: 1 | 2) =>
    filiationInconnueAutorisee(numeroParent, data?.codeAuteurReconnaissance);

  return (
    <div
      className="assistant-step-parents"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25em',
        width: '100%',
      }}
    >
      <InformationsParent
        numeroParent={1}
        libelleParent={roles?.[1].libelle}
        titreSection={roles?.[1].titreSection}
        disabledSexe={!!roles}
        filiationInconnuePossible={inconnuPossible(1)}
        parent={data?.parent1 ?? {}}
        filiationInconnue={data?.filiationInconnueParent1 ?? false}
        errors={errorsStep?.parent1 ?? {}}
        onChange={onChange}
        typeComponent={typeComponent}
        showProfession={showProfession}
        griserSiDecede={griserSiDecede}
      />
      <InformationsParent
        numeroParent={2}
        libelleParent={roles?.[2].libelle}
        titreSection={roles?.[2].titreSection}
        disabledSexe={!!roles}
        filiationInconnuePossible={inconnuPossible(2)}
        parent={data?.parent2 ?? {}}
        filiationInconnue={data?.filiationInconnueParent2 ?? false}
        errors={errorsStep?.parent2 ?? {}}
        onChange={onChange}
        typeComponent={typeComponent}
        showProfession={showProfession}
        griserSiDecede={griserSiDecede}
      />
    </div>
  );
};

export default StepParents;
